export const GRE_ARTICLE_PUBLIC_CODE_ALPHABET =
  'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
export const GRE_ARTICLE_PUBLIC_CODE_LENGTH = 5;
export const GRE_ARTICLE_PUBLIC_CODE_BASE =
  GRE_ARTICLE_PUBLIC_CODE_ALPHABET.length;
export const GRE_ARTICLE_PUBLIC_CODE_MODULUS =
  GRE_ARTICLE_PUBLIC_CODE_BASE ** GRE_ARTICLE_PUBLIC_CODE_LENGTH;
export const GRE_ARTICLE_PUBLIC_CODE_MULTIPLIER = 1597;
export const GRE_ARTICLE_PUBLIC_CODE_OFFSET = 5171;

const modInverse = (value, modulus) => {
  let t = 0;
  let newT = 1;
  let r = modulus;
  let newR = value;

  while (newR !== 0) {
    const quotient = Math.floor(r / newR);
    [t, newT] = [newT, t - quotient * newT];
    [r, newR] = [newR, r - quotient * newR];
  }

  if (r !== 1) {
    throw new Error('Value has no modular inverse');
  }

  return ((t % modulus) + modulus) % modulus;
};

const GRE_ARTICLE_PUBLIC_CODE_MULTIPLIER_INVERSE = modInverse(
  GRE_ARTICLE_PUBLIC_CODE_MULTIPLIER,
  GRE_ARTICLE_PUBLIC_CODE_MODULUS
);

const mod = (value, modulus) => ((value % modulus) + modulus) % modulus;

const encodeBase = (value) => {
  let remaining = value;
  let result = '';

  for (let i = 0; i < GRE_ARTICLE_PUBLIC_CODE_LENGTH; i += 1) {
    const index = remaining % GRE_ARTICLE_PUBLIC_CODE_BASE;
    result = `${GRE_ARTICLE_PUBLIC_CODE_ALPHABET[index]}${result}`;
    remaining = Math.floor(remaining / GRE_ARTICLE_PUBLIC_CODE_BASE);
  }

  return result;
};

const decodeBase = (code) =>
  code.split('').reduce((value, character) => {
    const index = GRE_ARTICLE_PUBLIC_CODE_ALPHABET.indexOf(character);
    if (index === -1) {
      throw new Error('Invalid GRE article public code');
    }

    return value * GRE_ARTICLE_PUBLIC_CODE_BASE + index;
  }, 0);

export const isGreArticlePublicCode = (value) => {
  if (typeof value !== 'string') {
    return false;
  }

  const normalized = value.trim().toUpperCase();
  if (normalized.length !== GRE_ARTICLE_PUBLIC_CODE_LENGTH) {
    return false;
  }

  return normalized
    .split('')
    .every((character) => GRE_ARTICLE_PUBLIC_CODE_ALPHABET.includes(character));
};

export const encodeGreArticlePublicCode = (articleId) => {
  const numericArticleId = Number(articleId);
  if (!Number.isInteger(numericArticleId) || numericArticleId < 0) {
    throw new Error('GRE article id must be a non-negative integer');
  }

  if (numericArticleId >= GRE_ARTICLE_PUBLIC_CODE_MODULUS) {
    throw new Error('GRE article id is out of range for public code encoding');
  }

  const obfuscatedValue =
    (numericArticleId * GRE_ARTICLE_PUBLIC_CODE_MULTIPLIER +
      GRE_ARTICLE_PUBLIC_CODE_OFFSET) %
    GRE_ARTICLE_PUBLIC_CODE_MODULUS;

  return encodeBase(obfuscatedValue);
};

export const decodeGreArticlePublicCode = (code) => {
  if (!isGreArticlePublicCode(code)) {
    throw new Error('Invalid GRE article public code');
  }

  const encodedValue = decodeBase(code.trim().toUpperCase());
  return mod(
    mod(
      encodedValue - GRE_ARTICLE_PUBLIC_CODE_OFFSET,
      GRE_ARTICLE_PUBLIC_CODE_MODULUS
    ) * GRE_ARTICLE_PUBLIC_CODE_MULTIPLIER_INVERSE,
    GRE_ARTICLE_PUBLIC_CODE_MODULUS
  );
};

export const normalizeGreArticlePublicCode = (value) => {
  if (typeof value !== 'string') {
    return null;
  }

  const normalized = value.trim().toUpperCase();
  return isGreArticlePublicCode(normalized) ? normalized : null;
};

export const resolveGreArticleRouteRef = (value) => {
  if (typeof value !== 'string' && typeof value !== 'number') {
    return null;
  }

  const normalized = String(value).trim();
  if (!normalized) {
    return null;
  }

  if (/^\d+$/.test(normalized)) {
    return encodeGreArticlePublicCode(Number(normalized));
  }

  return normalizeGreArticlePublicCode(normalized);
};

export const getGreArticlePublicRef = (article) => {
  if (!article || typeof article !== 'object') {
    return null;
  }

  if (article.public_code) {
    return normalizeGreArticlePublicCode(article.public_code);
  }

  if (Number.isInteger(article.id)) {
    return encodeGreArticlePublicCode(article.id);
  }

  return null;
};
